import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useMarketStore } from '../store/useMarketStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface BatchSourceFilters {
  multifamilyOnly: boolean;
  unitMin: number | null;
  unitMax: number | null;
  permitFilter: 'no_recent' | 'has_permits' | 'all';
  violationFilter: 'has_violations' | 'no_violations' | 'all';
  tiers: string[];
  zipCodes: string[];
  marketStatus: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
}

export const DEFAULT_BATCH_FILTERS: BatchSourceFilters = {
  multifamilyOnly: true,
  unitMin: null,
  unitMax: null,
  permitFilter: 'all',
  violationFilter: 'all',
  tiers: [],
  zipCodes: [],
  marketStatus: 'any',
  sortBy: 'cap_rate',
  sortOrder: 'desc',
  page: 1,
  limit: 25,
};

export interface BatchSourceLead {
  id: string;
  address: string;
  formatted_address: string | null;
  neighborhood: string | null;
  neighborhood_tier: string;
  zip_code: string | null;
  total_units: number;
  cap_rate: number | null;
  noi: number | null;
  lead_score: number | null;
  combined_score: number | null;
  has_recent_mep: boolean | null;
  mep_permit_count: number | null;
  has_violations: boolean | null;
  violation_count: number | null;
  is_multifamily: boolean | null;
  market_value: number | null;
  listing_status: string | null;
  year_built: number | null;
  estimated_monthly_rent: number | null;
  rent_confidence: string | null;
  rent_estimate_source: string | null;
}

interface BatchSourceResponse {
  total: number;
  page: number;
  limit: number;
  pages: number;
  leads: BatchSourceLead[];
  filter_counts: Record<string, unknown> | null;
}

function buildQueryString(filters: BatchSourceFilters, marketId: string): string {
  const params = new URLSearchParams();

  params.set('market_id', marketId);

  if (filters.tiers.length > 0) {
    params.set('tiers', filters.tiers.join(','));
  }

  if (filters.zipCodes.length > 0) {
    params.set('zip_codes', filters.zipCodes.join(','));
  }

  if (filters.unitMin !== null) {
    params.set('unit_min', String(filters.unitMin));
  }
  if (filters.unitMax !== null) {
    params.set('unit_max', String(filters.unitMax));
  }

  if (filters.marketStatus !== 'any') {
    params.set('market_status', filters.marketStatus);
  }

  params.set('sort_by', filters.sortBy);
  params.set('sort_order', filters.sortOrder);
  params.set('page', String(filters.page));
  params.set('limit', String(filters.limit));
  params.set('include_counts', 'true');

  return params.toString();
}

async function fetchBatchLeads(queryString: string): Promise<BatchSourceResponse> {
  const response = await fetch(`${API_BASE}/api/leads?${queryString}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export function useBatchSource(filters: BatchSourceFilters) {
  const activeMarket = useMarketStore((s) => s.activeMarket);
  const queryString = buildQueryString(filters, activeMarket.market_id);

  const query = useQuery<BatchSourceResponse, Error>({
    queryKey: ['batchSource', queryString],
    queryFn: () => fetchBatchLeads(queryString),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  return {
    leads: query.data?.leads ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? filters.page,
    pages: query.data?.pages ?? 0,
    filterCounts: query.data?.filter_counts,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
  };
}
